import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import { P, H3, H4 } from '@decisiv/ui-components/lib/components/Typography';

import Asterisk from '@decisiv/iconix/lib/components/Asterisk';

import getInputEventValue from 'utils/getInputEventValue';
import { createForm, Combobox } from 'components/Form';
import { useFormContext, useWatch } from 'react-hook-form';

import useDebouncedValue from 'hooks/useDebouncedValue';
import { API_SEARCH_DEBOUNCE_DELAY_MS } from 'pages/Management/constants';

import { useManagedDepotsFromPlatformAccountQuery } from '../hooks/useManagedDepotsFromPlatformAccountQuery';
import { useManagedPlatformAccountsQuery } from '../hooks/useManagedPlatformAccountsQuery';

import { RequiredColumn } from '../styles';

import { stepOneFormRef, useAddAssetContext } from '../context/AddAssetContext';
import { type StepOneFormValues } from '../types';
import { buildDepotOptions } from '../utils/buildDepotOptions';
import { buildAccountOptions } from '../utils/buildAccountOptions';
import { schema } from './schema';
import { useValidateVinsMutation } from './useValidateVinsMutation';
import VinTagInput from './VinTagInput';

export const { FormProvider, Form } = createForm<StepOneFormValues>();

function FormContent() {
  const [batchCreationError, setBatchCreationError] = useState<string | null>(
    null,
  );

  const [validateVins] = useValidateVinsMutation();

  const {
    setValue,
    formState: { isValid, isSubmitting },
  } = useFormContext();

  const {
    accountId,
    setSavedState,
    setIntermediate,
    setStepOneContinueButtonEnabled,
    setStepOneContinueButtonLoading,
    setStepTwoContinueButtonEnabled,
  } = useAddAssetContext();

  const accountLocked = !isNil(accountId);

  const selectedAccountId = useWatch({ name: 'accountId' }) as
    | string
    | undefined;

  // With the current implementation of the Wizard,
  // a useEffect will be needed to jurry-rig the "Continue" button state;
  useEffect(() => {
    setStepOneContinueButtonEnabled(isValid);
  }, [isValid, setStepOneContinueButtonEnabled]);

  useEffect(() => {
    setStepOneContinueButtonLoading(isSubmitting);
  }, [isSubmitting, setStepOneContinueButtonLoading]);

  const [, setAccountSearch, debouncedAccountSearch] = useDebouncedValue(
    '',
    API_SEARCH_DEBOUNCE_DELAY_MS,
  );

  const { data: accountsData, loading: loadingAccounts } =
    useManagedPlatformAccountsQuery({
      variables: {
        page: 1,
        ids: accountLocked ? [accountId as string] : undefined,
        text: !accountLocked ? debouncedAccountSearch || undefined : undefined,
      },
    });

  const accountOptions = useMemo(
    () => buildAccountOptions(accountsData),
    [accountsData],
  );

  const [, setDepotSearch, debouncedDepotSearch] = useDebouncedValue(
    '',
    API_SEARCH_DEBOUNCE_DELAY_MS,
  );

  const { data: depotsData, loading: loadingDepots } =
    useManagedDepotsFromPlatformAccountQuery({
      variables: {
        platformAccountIds: [selectedAccountId].filter(Boolean),
        page: 1,
        text: debouncedDepotSearch || undefined,
      },
      skip: !selectedAccountId,
    });

  // Track whether depots have loaded at least once so that subsequent
  // server-side searches (which momentarily clear `data` under the no-cache
  // policy) don't fully unmount the step on every keystroke-triggered refetch.
  const hasLoadedDepotsOnce = useRef(false);
  if (depotsData) hasLoadedDepotsOnce.current = true;

  const depotOptions = useMemo(
    () => buildDepotOptions(depotsData),
    [depotsData],
  );

  // When the account changes (searchable entry), the previously chosen depot no
  // longer belongs to the selected account, so clear it.
  const handleAccountChange = useCallback(() => {
    setValue('depotId', '', { shouldValidate: true });
    setDepotSearch('');
  }, [setValue, setDepotSearch]);

  const handleOnSubmit = useCallback(
    async (values: StepOneFormValues) => {
      setBatchCreationError(null);

      const { data } = await validateVins({
        variables: {
          assetGroupingId: values.depotId,
          vins: values.vins,
        },
      });

      const batchId = data?.validateVins?.batch?.id;

      if (!batchId) {
        // If batch creation failed, show error and stay on this step
        setBatchCreationError(
          t`We were unable to process your assets. Please try again.`,
        );
        return;
      }

      const accountName = accountOptions.find(
        (option) => option.value === values.accountId,
      )?.label;
      const depotName = depotOptions.find(
        (option) => option.value === values.depotId,
      )?.label;

      setSavedState((oldState) => ({
        ...oldState,
        stepOne: {
          accountId: values.accountId,
          depotId: values.depotId,
          vins: values.vins,
          accountName,
          depotName,
        },
        stepTwo: {
          batchId,
          validVins: [],
          invalidVins: [],
          usedByOther: [],
          usedByOtherDepot: [],
          ownedByCurrent: [],
          failed: false,
        },
      }));

      setStepTwoContinueButtonEnabled(false);
      // Enter the intermediate state: IdentifyingAssets polls the batch and
      // advances to Review when identification completes.
      setIntermediate(true);
    },
    [
      setIntermediate,
      setSavedState,
      validateVins,
      setStepTwoContinueButtonEnabled,
      accountOptions,
      depotOptions,
    ],
  );

  if (loadingDepots && !hasLoadedDepotsOnce.current && selectedAccountId)
    return <Loading size="large" title={t`Loading...`} />;

  return (
    <Form ref={stepOneFormRef} flexDirection="column" onSubmit={handleOnSubmit}>
      <RequiredColumn>
        <Asterisk color="information" />
        <P>
          <Trans>Required Fields</Trans>
        </P>
      </RequiredColumn>
      {batchCreationError && (
        <div style={{ marginBottom: '1rem' }}>
          <Alert intent="danger" title={t`Asset Validation Failed`}>
            {batchCreationError}
          </Alert>
        </div>
      )}
      <Flex flexDirection="column" marginBottom={1}>
        <H3>
          <Trans>Select Account and Depot</Trans>
        </H3>
        <P size="small" shade={1}>
          <Trans>
            Select the account and depot you want to add the asset(s) to.
          </Trans>
        </P>
      </Flex>
      <Combobox
        label={t`Account`}
        defaultValue=""
        name="accountId"
        options={accountOptions}
        variant="sync"
        loading={loadingAccounts}
        onChangeInputValue={(e) => setAccountSearch(getInputEventValue(e))}
        onChange={handleAccountChange}
        required
        zIndex={210}
      />
      <Flex marginTop={1}>
        <Combobox
          label={t`Depot`}
          defaultValue=""
          name="depotId"
          options={depotOptions}
          variant="sync"
          loading={loadingDepots}
          onChangeInputValue={(e) => setDepotSearch(getInputEventValue(e))}
          required
          zIndex={200}
          disabled={!selectedAccountId}
          warningMessage={
            selectedAccountId && isEmpty(depotOptions)
              ? /* istanbul ignore next -- @preserve */ t`No depots available.`
              : undefined
          }
        />
      </Flex>
      <Flex flexDirection="column" marginY={1}>
        <H4>
          <Trans>List of VINs</Trans>
        </H4>
        <P size="small" shade={1}>
          <Trans>
            Enter 17-character VINs separated by commas, spaces, or line breaks.
          </Trans>
        </P>
      </Flex>
      <VinTagInput />
    </Form>
  );
}

export default function StepOne() {
  const { savedState, accountId, depotId } = useAddAssetContext();

  return (
    <FormProvider
      validationSchema={schema}
      initialValues={{
        accountId: savedState.stepOne.accountId ?? accountId,
        depotId: savedState.stepOne.depotId ?? depotId,
        vins: savedState.stepOne.vins,
      }}
    >
      <FormContent />
    </FormProvider>
  );
}
